import { useEffect, useRef } from "react";
import { useMmGtm } from "../mm-gtm-context";
import { GtmDataLayerEvent } from "@made-people/grease-gtm";
import { useAccountState } from "@made-people/grease-account";
import { useLoaderData, useMatches } from "react-router";
import { HtmlPage, StoryblokPage } from "@made-people/nestjs-bff-service";
import { useCountriesAndLanguages } from "@made-people/grease-countries-and-languages";
import {
  gearsLoader,
  gearsMeta,
  useRootLoaderData,
} from "@made-people/gears-remix";
import type { loader as wildcardPageLoader } from "../../../routes/_index";
import {
  gbViewedExperimentDataLayerEventName,
  gbViewedExperimentsParamName,
} from "../../growthbook/constants";
import { useVisitorEmail } from "../../emarsys/mm-visitor-email-provider";

type WildcardPageLoaderData = Awaited<
  ReturnType<typeof wildcardPageLoader>
>["data"];

declare global {
  interface Window {
    _previousRoute: string;
  }
}

export const MmGtmEventPageView = () => {
  const { customer } = useAccountState();
  const email = useVisitorEmail();
  const { pusher, frontendContext } = useMmGtm();
  const { countries } = useCountriesAndLanguages();
  const loaderData = useLoaderData<typeof gearsLoader>();
  const rootLoaderData = useRootLoaderData();

  // Page type accessible here
  const matches = useMatches();
  const lastTracked = useRef<string | null>(null);

  const current = matches.find(
    (match) => match.id === "project-router-wildcard",
  );

  useEffect(() => {
    if (!current) {
      return;
    }

    const pathname = current.pathname;

    const page = (current.loaderData as WildcardPageLoaderData | undefined)
      ?.pageResponse?.page as HtmlPage;

    if (!pathname || !page) {
      return;
    }
    if (pathname === lastTracked.current) {
      return;
    }

    if (!("pageType" in page)) {
      return;
    }

    lastTracked.current = pathname;

    const meta = gearsMeta({
      loaderData,
      // @ts-expect-error I have no idea how to correctly type 'matches', but it works
      matches: [{ id: "root", loaderData: rootLoaderData }],
    });
    const pageName =
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (meta.find((m) => "title" in m) as any)?.title ?? page.meta.title;

    const country =
      countries.find(
        (country) => country.countryCode === frontendContext.country,
      )?.name ?? frontendContext.country;

    // Map page type to the ones from the old site
    const pageType =
      {
        centraProductPage: "product",
        centraCategoryPage: "category",
        storyblokPage:
          // Hack to check if we're on the home page
          `/${frontendContext.country.toLowerCase()}` ===
          window.location.pathname
            ? "home"
            : ((page as StoryblokPage)?.storyblokData?.content?.pageType ??
              "other"),
        checkoutIndex: "Basketpage",
        checkoutFinalize: "checkout-type",
        checkoutReceipt: "checkout-type",
        accountIndex: "ComfortClubIndex",
        accountLogin: "ComfortClubLogin",
        accountRegister: "ComfortClubRegister",
        forgotPassword: "ComfortClubResetPassword",
        searchPage: "search",
        notFound: "404",
      }[page.pageType as string] ?? "unknown";

    const pageReferrer = window._previousRoute || document.referrer;
    let triggerType = "hard";
    if (
      pageReferrer.startsWith(window.location.origin) &&
      pageReferrer !== window.location.href
    ) {
      triggerType = "soft";
    }

    const payload: GtmDataLayerEvent = {
      event: "pageView",

      pageInfo: {
        url: window.location.href,
        canonicalUrl: window.location.origin + window.location.pathname,
        pagePath: window.location.pathname,
        pageTitle: pageName,
        pageType,
        pageReferrer,
        countryCode: frontendContext.country,
        language: `${frontendContext.language}-${frontendContext.country}`,
        currency: frontendContext.currency.currency,
        triggerType,
        country,
      },
      environmentInfo: {
        isProduction: window.location.hostname.startsWith("www."),
      },
    };

    if (customer || email) {
      // How to make this work on initial page load since this fires before
      // the account xhr has finished loading?
      const { addresses, firstName, lastName, id } = customer ?? {};
      const address = addresses?.[0];
      const { city, country, phoneNumber, zipCode } = address ?? {};

      if (id) {
        payload.userId = id;
      }
      payload.user = {
        city,
        country,
        email,
        firstName,
        lastName,
        phoneNumber,
        zipCode,
      };
    }

    pusher(payload);
    window._previousRoute = payload.pageInfo.canonicalUrl;

    //
    // Push growthbook tracking that was passed via the URL from
    // a redirect URL test.
    //
    const viewedExperimentsParam = new URLSearchParams(
      window.location.search,
    ).get(gbViewedExperimentsParamName);

    const viewedExperiments = [];
    if (viewedExperimentsParam) {
      const parsed = JSON.parse(atob(viewedExperimentsParam));
      viewedExperiments.push(...parsed);
    }
    if (viewedExperiments.length > 0) {
      viewedExperiments.forEach((experiment) =>
        pusher({
          event: gbViewedExperimentDataLayerEventName,
          growthbook: {
            experimentId: experiment.experimentId,
            variationId: experiment.variationId,
          },
        }),
      );
      if (window.location.search.includes(gbViewedExperimentsParamName)) {
        // Remove gb-ve from query string, so we don't trigger this tracking again
        window.history.replaceState(
          window.history.state,
          "",
          window.location.pathname,
        );
      }
    }
  }, [current]);

  return null;
};
